import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import DashboardHeader from './DashboardHeader.jsx';
import { dataParser } from "./DataParser.js"
import Api from "../../api/Api.js";
import './Dashboard.css';

const TABLE_WRAPPER_STYLE = { maxHeight: '80vh', overflowY: 'auto' };
const TABLE_STYLE = { width: '97%' };
const ROW_HEIGHT_ESTIMATE = 40;
const OVERSCAN = 10;

const DataTable = ({ data2, expanded, handleYNClick, onBGUpdate }) => {
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);

  const tableRows = useMemo(() => {
    const data = dataParser(expanded, data2);
    if (data.length === 0) return [];
    const maxRow = Math.max(...data.map(item => item[0]));
    const maxCol = Math.max(...data.map(item => item[1]));
    const rows = Array.from({ length: maxRow }, (_, ri) =>
      Array.from({ length: maxCol }, (_, ci) => ({
        id: `cell-${ri + 1}-${ci + 1}`, value: '', task_ID: '', step_code: '', began_at: null, completed_at: null
      }))
    );
    data.forEach(([row, col, value, task_ID, step_code, detail, key]) => {
      const cell = rows[row - 1][col - 1];
      cell.value = value;
      cell.task_ID = task_ID;
      cell.step_code = step_code;
      cell.began_at = detail !== undefined ? detail["began_at"] : null;
      cell.completed_at = detail !== undefined ? detail["completed_at"] : null;
      cell.key = key ?? "";
    });
    return rows;
  }, [data2, expanded]);

  const rowVirtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ROW_HEIGHT_ESTIMATE,
    overscan: OVERSCAN,
    measureElement: (el) => {
      if (!el) return ROW_HEIGHT_ESTIMATE;
      return el.getBoundingClientRect().height;
    },
  });

  // Reset scroll and re-measure when expanded changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
    rowVirtualizer.measure();
  }, [expanded, rowVirtualizer]);

  const handleClick = useCallback(async (colIndex, cell) => {
    if (colIndex === 0) {
      if (window.confirm("수정창으로 이동할까요??"))
        navigate('/tasks/create?ID=' + cell.value);
    }
    if (cell.value === "Y" || cell.value === "N" || cell.value === "P") {
      handleYNClick(cell)
    }
    if (cell.key === "비고") {
      var bg = window.prompt('비고란을 입력해주세요');
      if (bg != null) {
        var ret2 = (await Api.taskBGPut(cell["task_ID"], cell["step_code"], bg)).data;
        if (!ret2["ret"]) {
          return alert(ret2["message"]);
        }
        if (onBGUpdate) {
          onBGUpdate(cell["task_ID"], cell["step_code"], bg);
        }
      }
    }
  }, [navigate, handleYNClick, onBGUpdate]);

  const handleTbodyClick = useCallback((e) => {
    const td = e.target.closest('td');
    if (!td) return;
    const row = parseInt(td.dataset.row, 10);
    const col = parseInt(td.dataset.col, 10);
    if (isNaN(row) || isNaN(col)) return;
    const cell = tableRows[row]?.[col];
    if (!cell) return;
    handleClick(col, cell);
  }, [tableRows, handleClick]);

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom = virtualItems.length > 0 ? totalSize - virtualItems[virtualItems.length - 1].end : 0;

  return (
    <div ref={scrollContainerRef} style={TABLE_WRAPPER_STYLE}>
      <table className='table table-sm table-bordered dashboard-table' id="table-to-xlsx" style={TABLE_STYLE}>
        <DashboardHeader expanded={expanded} />
        <tbody onClick={handleTbodyClick}>
          {paddingTop > 0 && (
            <tr><td style={{ height: paddingTop, padding: 0, border: 'none' }} colSpan={999} /></tr>
          )}
          {virtualItems.map((virtualRow) => {
            const rowIndex = virtualRow.index;
            const row = tableRows[rowIndex];
            if (!row) return null;
            const stripeClass = rowIndex % 2 === 1 ? 'dashboard-row-striped' : '';
            return (
              <tr
                key={row[0]?.id || `row-${rowIndex}`}
                data-index={rowIndex}
                ref={rowVirtualizer.measureElement}
                className={stripeClass}
              >
                {row.map((cell, colIndex) => (
                  <td key={cell.id} id={cell.id}
                      className={colIndex < (expanded ? 5 : 4) ? 'fw-bold' : ''}
                      data-row={rowIndex} data-col={colIndex}>
                    {(cell.value === "N") ?
                      (<div className='text-danger fw-bold'>{cell.value}</div>)
                    :
                      (<div>{cell.value}</div>)
                    }
                  </td>
                ))}
              </tr>
            );
          })}
          {paddingBottom > 0 && (
            <tr><td style={{ height: paddingBottom, padding: 0, border: 'none' }} colSpan={999} /></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DataTable;
